import { usePage } from "@inertiajs/react";
import type { ContextFn, Locale } from "date-fns";
import { compareDesc, format, parseISO } from "date-fns";
import { Activity, ChevronDownIcon, Trash2Icon } from "lucide-react";
import { type ComponentProps, memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDateContext } from "@/components/providers/date-provider";
import { Disclosure } from "@/components/shared/disclosure";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
	findReasonLabel,
	type ReasonOption,
} from "@/lib/appointments/reason-label";
import { cn } from "@/lib/utils";
import type {
	Appointment,
	RemovedVisitSnapshot,
} from "@/types/admin-portal/appointment";

function formatMetadataDatetime(
	val: string | null | undefined,
	locale: Locale,
	tzDate: ContextFn<Date>,
): string | null {
	if (!val) return null;
	try {
		const d = parseISO(val);
		return format(d, "PPPPppp", { locale, in: tzDate });
	} catch {
		return val;
	}
}

// Rows that record something other than a status change (the address, the SOAP, the
// evidence, the money): each gets its own sentence and no status label, since both sides
// are equal. Keep in step with AppointmentStatusHistory::NON_STATUS_EVENTS.
const NON_STATUS_EVENT_MESSAGES: Record<string, string> = {
	address_update: "address_updated",
	soap_update: "soap_updated",
	soap_delete: "soap_deleted",
	evidence_delete: "evidence_deleted",
	payment_update: "payment_updated",
	package_change: "package_changed",
	package_stop: "package_stopped",
	voucher_change: "voucher_changed",
	mitra_reason_change: "mitra_reason_changed",
	medical_record_update: "medical_record_updated",
};

// Event types whose metadata is worth unfolding under the sentence. An allow-list rather
// than "render whatever has metadata", so the delete rows keep showing only their sentence
// — their metadata is restore ids for tech, not something an admin reads.
const METADATA_EVENTS = new Set([
	"reschedule",
	"address_update",
	"soap_update",
	"payment_update",
	"package_change",
	"package_stop",
	"voucher_change",
	"mitra_reason_change",
	"medical_record_update",
]);

export interface HistoryListProps extends ComponentProps<"div"> {
	histories: Appointment["statusHistories"];
}

const HistoryList: React.FC<HistoryListProps> = memo(function Component({
	className,
	histories,
}) {
	const { locale, tzDate, timeFormatDateFns } = useDateContext();
	const { t } = useTranslation("appointments");

	const { grouped, sortedDates } = useMemo(() => {
		// Group by date
		const grouped: Record<string, Appointment["statusHistories"]> = {};
		for (const item of histories) {
			const dateKey = format(parseISO(item.changedAt), "yyyy-MM-dd", {
				locale,
				in: tzDate,
			});
			if (!grouped[dateKey]) {
				grouped[dateKey] = [];
			}
			grouped[dateKey].push(item);
		}

		// Sort dates descending
		const sortedDates = Object.keys(grouped).sort((a, b) =>
			compareDesc(parseISO(a, { in: tzDate }), parseISO(b, { in: tzDate })),
		);
		// Sort entries within each date descending by time
		for (const date of sortedDates) {
			grouped[date].sort((a, b) =>
				compareDesc(
					parseISO(a.changedAt, { in: tzDate }),
					parseISO(b.changedAt, { in: tzDate }),
				),
			);
		}

		return { grouped, sortedDates };
	}, [histories, locale, tzDate]);

	/** Make one nice sentence for each history row */
	const getHistoryMessage = useCallback(
		(h: HistoryListProps["histories"][number]) => {
			const nonStatusEvent = NON_STATUS_EVENT_MESSAGES[h.eventType ?? ""];
			if (nonStatusEvent) return t(`list.status_histories.${nonStatusEvent}`);

			if (!h.oldStatus || h.oldStatus === "UNSCHEDULED") {
				// first save / “created”
				return t("list.status_histories.created");
			}

			if (h.newStatus === "CANCELLED") {
				return t("list.status_histories.cancelled");
			}

			if (h.oldStatus !== h.newStatus) {
				// genuine transition
				return t("list.status_histories.updated");
			}

			// same → same  (e.g. an admin pressed “save” without changing)
			return t("list.status_histories.reapplied");
		},
		[t],
	);

	return (
		<div className={cn("w-full", className)}>
			{!sortedDates?.length ? (
				<div className="flex flex-col items-center justify-center rounded-md">
					<Activity className="mb-2 size-6 text-muted-foreground/75" />
					<p className="text-sm text-center text-muted-foreground">
						{t("list.status_histories.empty")}
					</p>
				</div>
			) : (
				sortedDates.map((date) => (
					<div key={date} className="mb-2">
						<p className="mb-4 font-medium">
							{format(date, "PPP", { locale, in: tzDate })}
						</p>

						<div className="relative">
							{grouped[date].map((item, index) => (
								<div key={`${date}-${String(index)}`} className="flex gap-4">
									{/* Timeline line and dot */}
									<div className="flex flex-col items-center mt-1">
										<div className="w-4 h-4 rounded-md bg-primary shrink-0" />
										{index !== grouped[date].length - 1 && (
											<div className="w-0.5 h-full bg-border mt-2" />
										)}
									</div>

									{/* Content */}
									<div className="flex-1 pb-4">
										<div className="flex justify-between gap-2">
											<p className="text-sm font-light">
												{format(item.changedAt, timeFormatDateFns, {
													locale,
													in: tzDate,
												})}
											</p>

											<p className="flex-1 font-medium text-right uppercase break-all line-clamp-1 text-pretty">
												{t("list.status_histories.by")}{" "}
												{item.changedBy.profile.name}
											</p>
										</div>

										<div className="my-1">
											<p className="text-pretty">
												<span>{getHistoryMessage(item)}</span>{" "}
												{item.newStatus !== "CANCELLED" &&
													!NON_STATUS_EVENT_MESSAGES[item.eventType ?? ""] && (
														<span className="font-medium">
															{t(
																`statuses.${item.newStatus.toLowerCase().replaceAll(" ", "_")}`,
															)}
														</span>
													)}
											</p>
										</div>

										{item.reason && (
											<div className="font-light">
												<p>
													{t("list.status_histories.reason")}: {item.reason}
												</p>
											</div>
										)}

										{METADATA_EVENTS.has(item.eventType ?? "") &&
											item.metadata && (
												<DetailsFold t={t}>
													<MetadataBlock
														metadata={item.metadata}
														t={t}
														locale={locale}
														tzDate={tzDate}
													/>
												</DetailsFold>
											)}
									</div>
								</div>
							))}
						</div>
					</div>
				))
			)}
		</div>
	);
});

function formatValue(
	key: string,
	val: unknown,
	nullLabel: React.ReactNode,
	locale: Locale,
	tzDate: ContextFn<Date>,
): React.ReactNode {
	if (val == null || val === "") return nullLabel;
	// React renders a raw boolean as nothing at all, which would have shown a SOAP flag
	// flipping from blank to blank.
	if (typeof val === "boolean") return val ? "Yes" : "No";

	switch (key) {
		case "appointmentDatetime":
			return formatMetadataDatetime(val as string, locale, tzDate) || nullLabel;
		case "visitNumber":
			return `#${val}`;
		case "therapistId":
			return (val as string).slice(0, 8);
		default:
			return val as React.ReactNode;
	}
}

const METADATA_LABELS: Record<string, string> = {
	appointmentDatetime: "Date & Time",
	status: "Status",
	visitNumber: "Visit",
	therapistId: "Therapist",
	therapistName: "Therapist",
	// Order-level events and the reschedule bypass flags
	action: "Action",
	applyToAllVisits: "Applied to all visits",
	bankCode: "Bank",
	bypassedChecks: "Checks bypassed",
	change: "Change",
	complaintDescription: "Complaint",
	condition: "Condition",
	discountAmount: "Discount",
	forceVoidSettledPayment: "Settled payment voided by hand",
	illnessOnsetDate: "Illness onset",
	medicalHistory: "Medical history",
	package: "Package",
	paymentDestination: "Destination account",
	paymentId: "Payment",
	paymentStatus: "Payment status",
	preset: "Settlement preset",
	removedVisits: "Visits deleted",
	source: "Source",
	totalAmount: "Total",
	stopReason: "Stop reason",
	refundAmount: "Refund",
	cancelledVisits: "Visits cancelled",
	visitCount: "Visits",
	voucherCode: "Voucher",
	mitraAssignmentReason: "Mitra assignment reason",
	description: "Description",
	city: "City",
	addressLine: "Address",
	postalCode: "Postal code",
	latitude: "Latitude",
	longitude: "Longitude",
	notes: "Notes",
	subject: "Subject",
	objective: "Objective",
	assessment: "Assessment",
	planning: "Planning",
	additionalNotes: "Additional notes",
	isFinalVisit: "Final visit",
	initialPhysicalCondition: "Initial physical condition",
	therapyGoalEvaluation: "Therapy goal evaluation",
	followUpTherapyPlan: "Follow-up therapy plan",
	nextPhysiotherapyGoals: "Next physiotherapy goals",
	therapyOutcomeSummary: "Therapy outcome summary",
	needsFollowUp: "Needs follow-up",
	recommendedVisits: "Recommended visits",
};

// "" and nil are the same absence; 0 (a coordinate) is a value.
const blankToNull = (value: unknown) =>
	value === "" || value === undefined ? null : value;

const NULL_LABELS: Record<string, string> = {
	appointmentDatetime: "Unscheduled",
	therapistId: "Unassigned",
	therapistName: "Unassigned",
};

// The fold on a single timeline row.
//
// Deliberately NOT the shared Disclosure: that one is a bordered card with a title and a
// hint line, right for one substantial payload and wrong repeated on every row of a
// timeline that already sits inside a card — five of them would be five cards inside a
// card. It keeps the part of Disclosure that matters, which its own source names: the
// affordance is a bounded surface that lights up, never loose text with a chevron.
//
// Closed by default. The sentence, the time, the author and the reason are what a reader
// scans; the field-by-field diff is what they open when one row turns out to matter.
function DetailsFold({
	t,
	children,
}: {
	t: ReturnType<typeof useTranslation>["t"];
	children: React.ReactNode;
}) {
	return (
		<Collapsible className="group mt-1.5">
			{/* 44px tall on touch widths and compact from lg, the same split the visit
			    action buttons use: below lg this panel is a drawer worked with a thumb. */}
			<CollapsibleTrigger className="inline-flex items-center gap-1 px-3 text-xs transition-colors border rounded-md min-h-11 lg:min-h-0 lg:px-2 lg:py-1 border-border text-muted-foreground hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1">
				<ChevronDownIcon className="size-3.5 transition-transform duration-200 group-data-[state=open]:rotate-180" />
				{/* Swapped by state rather than by a handler: the label is the only thing
				    that changes, and Radix already owns the open state and aria-expanded. */}
				<span className="group-data-[state=open]:hidden">
					{t("list.status_histories.show_details", "Show details")}
				</span>
				<span className="hidden group-data-[state=open]:inline">
					{t("list.status_histories.hide_details", "Hide details")}
				</span>
			</CollapsibleTrigger>

			<CollapsibleContent>{children}</CollapsibleContent>
		</Collapsible>
	);
}

// An old/new pair, as opposed to a plain fact about the action. Arrays are excluded on
// purpose: `removedVisits` is a list, not a change, and `"old" in val` would throw on it.
const isChange = (val: unknown): val is { old?: unknown; new?: unknown } =>
	typeof val === "object" &&
	val !== null &&
	!Array.isArray(val) &&
	("old" in val || "new" in val);

// Internal ids that identify a submission rather than describe it. Recorded so two rows
// written by one click can be tied together; nothing an admin reading the trail needs.
const HIDDEN_FACTS = new Set(["submissionId"]);

// A flat value: which checks were overridden, which preset was picked, whether a settled
// payment was voided by hand. No arrow, because there is no "before".
function FactRow({
	label,
	children,
}: {
	label: React.ReactNode;
	children: React.ReactNode;
}) {
	return (
		<div className="flex flex-col">
			<p className="text-muted-foreground font-medium text-pretty leading-normal">
				{label}
			</p>
			<p className="text-primary text-pretty leading-normal break-words">
				{children}
			</p>
		</div>
	);
}

// The documents a deleted visit was carrying, labelled with the `section_*` keys.
const REMOVED_VISIT_DOCUMENTS = [
	{ flag: "hadSoap", key: "soap", fallback: "SOAP" },
	{ flag: "hadEvidence", key: "evidence", fallback: "Evidence" },
	{
		flag: "hadMedicalRecord",
		key: "medicalRecord",
		fallback: "Medical record",
	},
] as const;

// What a package downgrade destroyed: one line per visit, with the documents it carried.
function RemovedVisits({
	visits,
	t,
}: {
	visits: RemovedVisitSnapshot[];
	t: ReturnType<typeof useTranslation>["t"];
}) {
	return (
		<div className="space-y-2">
			{visits.map((visit, index) => {
				const documents = REMOVED_VISIT_DOCUMENTS.filter(
					(doc) => visit[doc.flag],
				)
					.map((doc) =>
						t(
							`list.status_histories.metadata.section_${doc.key}`,
							doc.fallback,
						),
					)
					.join(", ");

				return (
					<div
						key={`${visit.visitNumber ?? "visit"}-${String(index)}`}
						// Tint without a border: these sit inside the Disclosure's own card,
						// and a bordered block in a bordered card is a card in a card.
						className="p-2 rounded-md bg-destructive/5"
					>
						<p className="font-medium">
							{t("list.status_histories.metadata.visitNumber", "Visit")} #
							{visit.visitNumber ?? "?"}
							{visit.status ? (
								<span className="text-muted-foreground font-normal">
									{" "}
									· {visit.status}
								</span>
							) : null}
						</p>

						{documents ? (
							<p>{documents}</p>
						) : (
							<p className="text-muted-foreground italic">
								{t(
									"list.status_histories.metadata.no_documents",
									"No documents on this visit",
								)}
							</p>
						)}
					</div>
				);
			})}
		</div>
	);
}

function MetadataBlock({
	metadata,
	t,
	locale,
	tzDate,
}: {
	metadata: NonNullable<HistoryListProps["histories"][number]["metadata"]>;
	t: ReturnType<typeof useTranslation>["t"];
	locale: Locale;
	tzDate: ContextFn<Date>;
}) {
	const { i18n } = useTranslation();
	const mitraReasons = usePage<{ mitraAssignmentReasons?: ReasonOption[] }>()
		.props.mitraAssignmentReasons;

	return (
		// Two columns from lg, not three: these entries are wildly uneven — "Visits 4 → 1"
		// beside a date range of ~105 characters. At three columns each cell is ~350px and
		// the long ones become four-line ribbons while the short ones waste most of theirs.
		<div className="grid gap-x-6 gap-y-2 mt-2 text-xs border-l-2 border-primary/30 pl-3 lg:grid-cols-2">
			{Object.entries(metadata as Record<string, unknown>).map(([key, raw]) => {
				const label = t(
					`list.status_histories.metadata.${key}`,
					METADATA_LABELS[key] || key,
				);

				// The visits a downgrade deleted, with the documents they were carrying.
				if (key === "removedVisits") {
					const visits = (raw as RemovedVisitSnapshot[]) ?? [];
					if (!visits.length) return null;

					return (
						// Full width: this is a set of blocks, not a label/value pair. A fold of
						// its own, and the shared Disclosure this time — one substantial payload
						// is exactly what that component is for, and its hint line can say what
						// is behind the fold before anyone spends a click finding out.
						<div key={key} className="lg:col-span-2">
							<Disclosure
								icon={Trash2Icon}
								title={`${label} (${visits.length})`}
								hint={t("list.status_histories.metadata.removed_visits_hint", {
									defaultValue:
										"Which documents these visits were carrying. Their content was deleted with them.",
								})}
								contentClassName="px-2 py-2"
							>
								<RemovedVisits visits={visits} t={t} />
							</Disclosure>
						</div>
					);
				}

				// A plain fact rather than a change. Booleans only show when true: "settled
				// payment voided by hand: No" is noise on every ordinary payment row.
				if (!isChange(raw)) {
					if (raw === null || raw === undefined || raw === "") return null;
					if (HIDDEN_FACTS.has(key)) return null;
					if (typeof raw === "boolean") {
						return raw ? (
							<FactRow key={key} label={label}>
								Yes
							</FactRow>
						) : null;
					}
					if (Array.isArray(raw)) {
						if (!raw.length) return null;
						return (
							<FactRow key={key} label={label}>
								{(raw as string[])
									.map((entry) =>
										t(
											`list.status_histories.metadata.${entry}`,
											entry.replaceAll("_", " "),
										),
									)
									.join(", ")}
							</FactRow>
						);
					}
					// Anything else is a shape this timeline has no label for. Printing it
					// would render "[object Object]" next to a real field name, which reads
					// like recorded data rather than a gap.
					if (raw !== null && typeof raw === "object") return null;
					return (
						<FactRow key={key} label={label}>
							{String(raw)}
						</FactRow>
					);
				}

				const val = raw as { old?: unknown; new?: unknown };

				// Blank on both sides is no change: older rows recorded a snapshot's "" against nil.
				if (blankToNull(val.old) === blankToNull(val.new)) return null;

				// Skip therapistId if therapistName is present
				if (key === "therapistId" && "therapistName" in metadata) return null;
				const nullLabel = (
					<span className="text-muted-foreground/50 italic">
						{NULL_LABELS[key] || "—"}
					</span>
				);

				const display = (v: unknown) =>
					formatValue(
						key,
						key === "mitraAssignmentReason"
							? findReasonLabel(mitraReasons, v as string, i18n.language)
							: v,
						nullLabel,
						locale,
						tzDate,
					);
				const oldDisplay = display(val.old);
				const newDisplay = display(val.new);

				return (
					<div key={key} className="flex flex-col">
						<p className="text-muted-foreground font-medium text-pretty leading-normal">
							{label}
						</p>
						<p className="text-pretty leading-normal">
							<span className="text-muted-foreground/75">{oldDisplay}</span>
							<span className="text-primary font-medium shrink-0 sm:inline-block hidden mx-1">
								→
							</span>
							<span className="sm:hidden text-muted-foreground/60 mx-1">
								to
							</span>
							<span className="text-primary">{newDisplay}</span>
						</p>
					</div>
				);
			})}
		</div>
	);
}

export default HistoryList;
